import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { useAuth } from '../../hooks/useAuth'
import WheelLoader from '../common/WheelLoader'
import GoogleButton from './GoogleButton'
import PasswordRequirements from './PasswordRequirements'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'
const IS_TEST_KEY = TURNSTILE_SITE_KEY.startsWith('1x')

export default function RegisterForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(
    IS_TEST_KEY ? 'test-token' : null
  )
  const turnstileRef = useRef<TurnstileInstance>(null)
  const { register } = useAuth()
  const navigate = useNavigate()

  // Auto-set token for test keys (used in development/testing)
  useEffect(() => {
    if (IS_TEST_KEY && !turnstileToken) {
      setTurnstileToken('test-token')
    }
  }, [turnstileToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== passwordConfirmation) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (!turnstileToken) {
      setError('Please wait for verification to complete')
      return
    }

    setLoading(true)

    try {
      await register(email, password, passwordConfirmation, turnstileToken)
      navigate('/home')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: { full_messages?: string[] } } } }
      setError(error.response?.data?.errors?.full_messages?.join(', ') || 'Registration failed.')
      // Reset turnstile on error
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-sm">
      {/* Card */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-5 sm:p-6">
        {/* Header */}
        <div className="text-center mb-5">
          <h1
            className="text-2xl sm:text-3xl text-black mb-1"
            style={{ fontFamily: "'Instrument Serif', serif", lineHeight: '1.1' }}
          >
            Create account
          </h1>
          <p className="text-sm text-black/60">Get started in minutes</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300">
            <p className="text-xs text-red-800 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </p>
          </div>
        )}

        <GoogleButton mode="signup" />

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-black/10"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-3 bg-white/70 text-black/50">or continue with email</span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-3"
          data-turnstile-ready={turnstileToken ? 'true' : 'false'}
        >
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-black/70 mb-1">
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-black/70 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
              className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
              placeholder="Min. 6 characters"
            />
          </div>

          <div>
            <label
              htmlFor="passwordConfirmation"
              className="block text-sm font-medium text-black/70 mb-1"
            >
              Confirm password
            </label>
            <input
              id="passwordConfirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
              placeholder="Re-enter password"
            />
            <PasswordRequirements
              password={password}
              passwordConfirmation={passwordConfirmation}
              showConfirmation={true}
            />
          </div>

          {/* Invisible Turnstile - shows challenge only when needed */}
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onError={() => setTurnstileToken(null)}
            onExpire={() => setTurnstileToken(null)}
            options={{
              size: 'invisible',
            }}
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <WheelLoader width={20} />
                Creating account...
              </span>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <div className="mt-5 pt-4 border-t border-black/10">
          <p className="text-center text-xs text-black/60">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-black hover:text-black/70 transition-colors"
            >
              Sign in instead
            </Link>
          </p>
        </div>
      </div>

      {/* Terms */}
      <p className="mt-4 text-center text-xs text-white/70">
        By creating an account, you agree to our{' '}
        <Link to="/terms" className="underline hover:text-white">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="underline hover:text-white">
          Privacy Policy
        </Link>
      </p>
    </div>
  )
}
